import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DownloadIcon } from './Icons';

// A call-recording player dressed as a 1950s walnut tabletop radio: push
// button on the left, analog tuning dial (the needle is the playback
// position, and the dial is the seek control), volume knob on the right,
// and two small keys along the bottom for playback speed and download.
//
// Every bit of playback state is read back from the <audio> element's own
// events rather than assumed from button clicks, so anything that pauses the
// element from outside (another recording starting, the browser) is
// reflected here too.

// AM broadcast dial, kc/10, as printed on period sets.
const DIAL_NUMBERS = [55, 60, 70, 80, 100, 120, 140, 160];
const SCALE_LEFT = 8;
const SCALE_RIGHT = 232;
const BAR_COUNT = 30;
// Fixed resting heights so the bars look like a frozen reading, not a row.
const BAR_REST = Array.from({ length: BAR_COUNT }, (_, i) => 0.28 + 0.5 * Math.abs(Math.sin(i * 1.7 + 0.4)));
const KNOB_SWEEP = 135; // degrees either side of straight up

// Speed key steps through these, wrapping round. The browser keeps the
// voice's pitch, so 1.5× still sounds like the caller, just faster.
export const SPEEDS = [1, 1.25, 1.5, 2, 0.75];
const RATE_STORAGE_KEY = 'recording_playback_rate';

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

function readStoredRate() {
  try {
    const stored = Number(localStorage.getItem(RATE_STORAGE_KEY));
    return SPEEDS.includes(stored) ? stored : 1;
  } catch {
    return 1;
  }
}

const formatRate = (rate) => `${rate}×`;

// "call-recording_919800000001_2026-10-12_12-19" from the customer number and
// the call's start_stamp ("2026-10-12 12:19:13"). No extension.
export function recordingFileName(customerNumber, startStamp) {
  const digits = String(customerNumber || '').replace(/\D/g, '');
  const when = String(startStamp || '')
    .slice(0, 16)
    .replace(/[^0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .replace(/^(\d{4}-\d{2}-\d{2})-/, '$1_');
  return ['call-recording', digits, when].filter(Boolean).join('_');
}

// Tata labels its MP3s with a list of types ("audio/mp3, audio/x-mpeg, …"),
// so only an unambiguous other format changes the extension.
function recordingExtension(type) {
  const t = String(type || '').toLowerCase();
  if (t.includes('wav')) return 'wav';
  if (t.includes('ogg')) return 'ogg';
  return 'mp3';
}

export function formatClock(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '--:--';
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
}

// Only one recording plays at a time: starting one pauses any other
// recording player on the page (radio or the plain table player).
export function pauseOtherRecordings(current) {
  document.querySelectorAll('audio[data-recording-audio]').forEach((audio) => {
    if (audio !== current && !audio.paused) audio.pause();
  });
}

function DialScale() {
  const ticks = [];
  for (let i = 0; i <= 56; i += 1) {
    const x = SCALE_LEFT + ((SCALE_RIGHT - SCALE_LEFT) * i) / 56;
    const major = i % 8 === 0;
    ticks.push(
      <line key={i} x1={x} x2={x} y1={major ? 15 : 17.5} y2={21} strokeWidth={major ? 0.9 : 0.5} />
    );
  }
  return (
    <svg className="vintage-radio-scale" viewBox="0 0 240 24" aria-hidden="true" focusable="false">
      <line x1={SCALE_LEFT} x2={SCALE_RIGHT} y1={21} y2={21} strokeWidth={0.6} />
      {ticks}
      {DIAL_NUMBERS.map((n, i) => (
        <text key={n} x={SCALE_LEFT + ((SCALE_RIGHT - SCALE_LEFT) * i) / 7} y={11} textAnchor="middle">
          {n}
        </text>
      ))}
    </svg>
  );
}

function Screw({ className }) {
  return <span className={`vintage-radio-screw ${className}`} aria-hidden="true" />;
}

function VolumeKnob({ value, onChange }) {
  const dragRef = useRef(null);

  const handlePointerDown = (event) => {
    event.preventDefault();
    event.currentTarget.focus();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragRef.current = { y: event.clientY, value };
  };
  const handlePointerMove = (event) => {
    if (!dragRef.current) return;
    // Drag up to turn clockwise; 120px covers the full sweep.
    onChange(clamp(dragRef.current.value + (dragRef.current.y - event.clientY) / 120, 0, 1));
  };
  const handlePointerUp = () => {
    dragRef.current = null;
  };
  const handleKeyDown = (event) => {
    const step = { ArrowUp: 0.05, ArrowRight: 0.05, ArrowDown: -0.05, ArrowLeft: -0.05, PageUp: 0.2, PageDown: -0.2 }[event.key];
    if (step !== undefined) {
      event.preventDefault();
      onChange(clamp(Math.round((value + step) * 100) / 100, 0, 1));
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      onChange(event.key === 'Home' ? 0 : 1);
    }
  };

  const percent = Math.round(value * 100);
  return (
    <div className="vintage-radio-control">
      <div
        className="vintage-radio-knob"
        role="slider"
        tabIndex={0}
        aria-label="Volume"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        aria-valuetext={`${percent}%`}
        title={`Volume ${percent}%`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onKeyDown={handleKeyDown}
      >
        <span
          className="vintage-radio-knob-cap"
          style={{ transform: `rotate(${-KNOB_SWEEP + value * KNOB_SWEEP * 2}deg)` }}
        />
      </div>
      <span className="vintage-radio-label">Vol</span>
    </div>
  );
}

function VintageRadioPlayer({ url, downloadName }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(NaN);
  const [volume, setVolume] = useState(1);
  const [error, setError] = useState('');
  const [rate, setRate] = useState(readStoredRate);
  const [downloading, setDownloading] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);
  const downloadAbortRef = useRef(null);

  // A different recording on the same instance starts from a clean slate.
  useEffect(() => {
    setPlaying(false);
    setBuffering(false);
    setCurrentTime(0);
    setDuration(NaN);
    setError('');
  }, [url]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  // defaultPlaybackRate too, so a reload (Play's retry after an error) keeps it.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.defaultPlaybackRate = rate;
    audio.playbackRate = rate;
  }, [rate]);

  useEffect(() => {
    if (!downloadFailed) return undefined;
    const timer = setTimeout(() => setDownloadFailed(false), 5000);
    return () => clearTimeout(timer);
  }, [downloadFailed]);

  // Closing the player mid-download drops the download.
  useEffect(() => () => downloadAbortRef.current?.abort(), []);

  const readDuration = useCallback(() => {
    const audio = audioRef.current;
    if (audio) setDuration(audio.duration);
  }, []);

  const handleToggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (!audio.paused) {
      audio.pause();
      return;
    }
    if (error) {
      setError('');
      audio.load();
    }
    setBuffering(true);
    const attempt = audio.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch((err) => {
        setBuffering(false);
        // A pause during loading rejects with AbortError; that's not a fault.
        if (err && err.name === 'AbortError') return;
        if (err && err.name === 'NotAllowedError') {
          setError('The browser blocked playback. Press play again.');
          return;
        }
        setError('Recording unavailable');
      });
    }
  };

  const handleSpeed = () => {
    const next = SPEEDS[(SPEEDS.indexOf(rate) + 1) % SPEEDS.length];
    setRate(next);
    try {
      localStorage.setItem(RATE_STORAGE_KEY, String(next));
    } catch {
      // private window / storage blocked: the speed just isn't remembered
    }
  };

  // Tata serves recordings with Access-Control-Allow-Origin: *, so the file
  // can be fetched here and saved under a readable name. A plain
  // <a download> to another origin would only open the audio in a tab.
  const handleDownload = async () => {
    if (downloading) return;
    const controller = new AbortController();
    downloadAbortRef.current = controller;
    setDownloading(true);
    setDownloadFailed(false);
    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const href = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = href;
      link.download = `${downloadName || 'call-recording'}.${recordingExtension(blob.type)}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(href), 1000);
    } catch {
      if (!controller.signal.aborted) setDownloadFailed(true);
    } finally {
      if (downloadAbortRef.current === controller) downloadAbortRef.current = null;
      if (!controller.signal.aborted) setDownloading(false);
    }
  };

  const handleSeek = (event) => {
    const audio = audioRef.current;
    const next = Number(event.target.value);
    if (!audio || !Number.isFinite(next)) return;
    audio.currentTime = next;
    setCurrentTime(next);
  };

  const handleError = () => {
    setPlaying(false);
    setBuffering(false);
    const code = audioRef.current?.error?.code;
    setError(code === 2 ? 'Network error loading recording' : 'Recording unavailable');
  };

  const canSeek = Number.isFinite(duration) && duration > 0 && !error;
  const progress = canSeek ? clamp(currentTime / duration, 0, 1) : 0;
  let status = 'Call recording';
  if (buffering) status = 'Tuning in…';
  if (downloading) status = 'Downloading…';
  if (downloadFailed) status = 'Download failed';

  return (
    <div className={`vintage-radio${playing ? ' is-playing' : ''}${error ? ' has-error' : ''}`}>
      <audio
        ref={audioRef}
        src={url}
        preload="metadata"
        data-recording-audio=""
        onPlay={(event) => {
          pauseOtherRecordings(event.currentTarget);
          setPlaying(true);
        }}
        onPlaying={() => setBuffering(false)}
        onPause={() => {
          setPlaying(false);
          setBuffering(false);
        }}
        onEnded={() => setPlaying(false)}
        onWaiting={() => setBuffering(true)}
        onCanPlay={() => setBuffering(false)}
        onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
        onLoadedMetadata={readDuration}
        onDurationChange={readDuration}
        onError={handleError}
      />

      <div className="vintage-radio-grille" aria-hidden="true">
        <span />
        <span />
        <span />
      </div>

      <div className="vintage-radio-panel">
        <Screw className="is-tl" />
        <Screw className="is-tr" />
        <Screw className="is-bl" />
        <Screw className="is-br" />

        <div className="vintage-radio-control">
          <button
            type="button"
            className="vintage-radio-play"
            onClick={handleToggle}
            aria-label={playing ? 'Pause recording' : 'Play recording'}
            aria-pressed={playing}
            title={playing ? 'Pause' : 'Play'}
          >
            {playing ? (
              <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false">
                <rect x="6" y="5" width="4" height="14" rx="0.6" />
                <rect x="14" y="5" width="4" height="14" rx="0.6" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>
          <span className="vintage-radio-label">{playing ? 'Pause' : 'Play'}</span>
        </div>

        <div className="vintage-radio-dial">
          <DialScale />
          <div className="vintage-radio-bars" aria-hidden="true">
            {BAR_REST.map((rest, i) => (
              <span
                key={i}
                style={{
                  '--bar-rest': rest,
                  '--bar-speed': `${0.55 + (i % 5) * 0.13}s`,
                  '--bar-delay': `${-((i * 0.37) % 1.1)}s`,
                }}
              />
            ))}
          </div>
          {error && <div className="vintage-radio-error" role="alert">{error}</div>}
          <div className="vintage-radio-track">
            <div className="vintage-radio-rail" aria-hidden="true">
              <span style={{ width: `${progress * 100}%` }} />
            </div>
            <span className="vintage-radio-needle" style={{ left: `${progress * 100}%` }} aria-hidden="true" />
            <input
              type="range"
              className="vintage-radio-seek"
              min={0}
              max={canSeek ? duration : 0}
              step={0.1}
              value={canSeek ? Math.min(currentTime, duration) : 0}
              onChange={handleSeek}
              disabled={!canSeek}
              aria-label="Seek recording"
              aria-valuetext={`${formatClock(currentTime)} of ${formatClock(duration)}`}
            />
          </div>
        </div>

        <VolumeKnob value={volume} onChange={setVolume} />

        <div className="vintage-radio-footer">
          <span className="vintage-radio-readout">
            <span className="vintage-radio-lamp" aria-hidden="true" />
            <span className="vintage-radio-time">{formatClock(currentTime)}</span>
            <span className="vintage-radio-sep" aria-hidden="true">/</span>
            <span className="vintage-radio-time">{formatClock(duration)}</span>
          </span>
          <span className="vintage-radio-nameplate" aria-live="polite">{status}</span>
          <span className="vintage-radio-keys">
            <button
              type="button"
              className={`vintage-radio-key vintage-radio-speed${rate !== 1 ? ' is-active' : ''}`}
              onClick={handleSpeed}
              aria-label={`Playback speed ${formatRate(rate)}, change speed`}
              title="Playback speed"
            >
              {formatRate(rate)}
            </button>
            <button
              type="button"
              className={`vintage-radio-key vintage-radio-download${downloadFailed ? ' is-failed' : ''}`}
              onClick={handleDownload}
              disabled={downloading}
              aria-label={downloading ? 'Downloading recording' : 'Download recording'}
              title={downloadFailed ? 'Download failed, try again' : 'Download recording'}
            >
              <DownloadIcon width={11} height={11} aria-hidden="true" focusable="false" />
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}

export default VintageRadioPlayer;
